<template>
  <vue-final-modal
    id="preview-modal"
    name="preview-modal"
    class="preview-modal"
    :class="{ maximized: maximized === true || mobile}"
    :esc-to-close="true"
    :click-to-close="true"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    @opened="opened"
  >
    <share-overlay
      v-if="displayShareOverlay"
      :url="urlToShare"
      @close="displayShareOverlay = false"
    >
      <template #share-overlay-content>
        <p>{{ shareLabel }}</p>
        <div
          id="icon-row"
          class="preview-modal__icons"
        >
          <a
            class="share-download-link"
            :content-download-url="url"
            :content-download-type="type"
            target="_blank"
            :href="'https://www.linkedin.com/shareArticle?url=' + urlToShare"
            :aria-label="`${shareLabel} linkedin`"
            @click="pushDataLayer('linkedin', 'logo')"
          >
            <img
              class="icon"
              :src="linkedin"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="url"
            :content-download-type="type"
            target="_blank"
            :href="'https://www.twitter.com/intent/tweet?url=' + urlToShare"
            :aria-label="`${shareLabel} twitter`"
            @click="pushDataLayer('twitter', 'logo')"
          >
            <img
              class="icon"
              :src="twitter"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="url"
            :content-download-type="type"
            target="_blank"
            :href="'https://www.facebook.com/sharer/sharer.php?u=' + urlToShare"
            :aria-label="`${shareLabel} facebook`"
            @click="pushDataLayer('facebook', 'logo')"
          >
            <img
              class="icon"
              :src="facebook"
              alt=""
            >
          </a>
          <a
            class="share-download-link"
            :content-download-url="url"
            :content-download-type="type"
            :href="'mailto:?Subject=' + shareEmailSubject + '&amp;body=' + urlToShare"
            :aria-label="`${shareLabel} email`"
            @click="pushDataLayer('email', 'logo')"
          >
            <img
              class="icon"
              :src="email"
              alt=""
            >
          </a>
        </div>
        <copy-to-clipboard :text="urlToShare">
          <template #click-to-copy>
            <a
              class="preview-modal__shareButton"
              :content-download-url="urlToShare"
              :content-download-type="type"
              @click="pushDataLayer(shareButtonLabel, 'button')"
            >{{ shareButtonLabel }}</a>
          </template>
          <template #result>
            <p>{{ shareButtonResultText }}</p>
          </template>
        </copy-to-clipboard>
      </template>
    </share-overlay>

    <div class="preview-modal__vfm-container">
      <div
        ref="topBar"
        class="top-bar"
      >
        <a
          v-if="!withoutShare && (type === 'pdf' ? url : urlToShare)"
          class="preview-modal__shareButton"
          href="#"
          @click="openShareOverlay"
        >{{ shareLabel }}</a>
        <div class="right-buttons">
          <button
            v-if="!mobile && type === 'pdf'"
            class="preview-modal-button maximize-button"
            :class="{ maximized: maximized === true}"
            @click="toggleMaximize()"
          >
            <svg
              fill="#FFFFFF"
              width="16px"
              height="16px"
              enable-background="new 0 0 384 384"
              version="1.1"
              viewBox="0 0 384 384"
              xml:space="preserve"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="m373.33 0h-234.67c-5.896 0-10.667 4.771-10.667 10.667s4.771 10.667 10.667 10.667h208.92l-344.46 344.46c-4.167 4.167-4.167 10.917 0 15.083 2.083 2.083 4.813 3.125 7.542 3.125s5.458-1.042 7.542-3.125l344.46-344.46v208.92c0 5.896 4.771 10.667 10.667 10.667s10.667-4.771 10.667-10.667v-234.67c-1e-3 -5.896-4.772-10.667-10.668-10.667z" />
            </svg>
          </button>
          <button
            class="preview-modal-button"
            @click="closeModal"
          >
            <svg
              width="16px"
              height="16px"
              viewBox="0 0 30 30"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs />
              <g
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  transform="translate(-1530.000000, -40.000000)"
                  fill="#FFFFFF"
                >
                  <path
                    d="M1545.8,54.2 L1564.20098,54.2 C1564.64227,54.2 1565,54.5550919 1565,55 C1565,55.4418278 1564.64198,55.8 1564.20098,55.8 L1545.8,55.8 L1545.8,74.2009848 C1545.8,74.6422687 1545.44491,75 1545,75 C1544.55817,75 1544.2,74.6419794 1544.2,74.2009848 L1544.2,55.8 L1525.79902,55.8 C1525.35773,55.8 1525,55.4449081 1525,55 C1525,54.5581722 1525.35802,54.2 1525.79902,54.2 L1544.2,54.2 L1544.2,35.7990152 C1544.2,35.3577313 1544.55509,35 1545,35 C1545.44183,35 1545.8,35.3580206 1545.8,35.7990152 L1545.8,54.2 Z"
                    transform="translate(1545.000000, 55.000000) rotate(-315.000000) translate(-1545.000000, -55.000000) "
                  />
                </g>
              </g>
            </svg>
          </button>
        </div>
      </div>

      <pdf-preview
        v-if="type === 'pdf'"
        :url="url"
      />

      <div
        v-if="type === 'video'"
        style="height: calc(100% - 60px)"
      >
        <vt-video
          :videos="medias"
          :lazy="true"
          :autoplay="true"
          :disable-share="true"
          :preferr="preferr"
          :sr-label="srLabel"
          :poster="poster"
        />
      </div>

      <div
        v-if="type === 'image'"
        style="height: calc(100% - 60px);"
      >
        <div
          class="image"
          :style="{ backgroundImage: 'url(' + url + ')' }"
        />
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { VueFinalModal } from 'vue-final-modal'
import facebook from '@images/facebook.svg'
import twitter from '@images/twitter.svg'
import email from '@images/email.svg'
import linkedin from '@images/linkedin.svg'
import ShareOverlay from '@molecules/share-overlay/share-overlay.vue'
import emitter from '../../../emitter'

export default {
  name: 'preview-modal',
  components: {
    ShareOverlay,
    VueFinalModal,
  },
  props: {
    medias: Object,
    poster: String,
    preferr: String,
    srLabel: String,
    type: String,
    url: String,
    share: Object,
    withoutShare: {
      type: Boolean,
      default: true
    },
    shareUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['openPdfPreview', 'closeVideoPreview', 'closemodal'],
  data() {
    return {
      maximized: Boolean,
      mobile: true,
      maxWidth: 1024,
      displayShareOverlay: false,
      facebook,
      twitter,
      linkedin,
      email,
      urlToShare: ''
    }
  },
  computed: {
    ...mapGetters('shareConfig', [
      'shareLabel',
      'shareButtonLabel',
      'shareButtonResultText',
      'shareEmailSubject'
    ])
  },
  watch: {
    mobile(mobile) {
      this.maximized = mobile
    }
  },
  created() {
    emitter.$on('closeShareOverlay', () => {
      this.displayShareOverlay = false
    })
    this.checkmobile()
    window.addEventListener('resize', this.checkmobile, false)
  },
  methods: {
    openShareOverlay() {
      this.displayShareOverlay = true

      this.$nextTick(() => {
        const iconRow = document.getElementById('icon-row')

        if (iconRow) {
          if (iconRow.childNodes.length > 0) {
            iconRow.childNodes[0].focus()
          }
        }
      })

      this.pushDataLayerOpenShare()
    },

    closeModal() {
      emitter.$emit('closemodal')
    },

    opened() {
      this.$refs.topBar.focus()
      if (this.type === 'pdf') {
        emitter.$emit('openPdfPreview', this.url)
      }
    },
    beforeOpen() {
      this.urlToShare = this.share ? this.share.url : this.shareUrl
      document.body.classList.add('modal-opened')
    },
    beforeClose() {
      if (this.type === 'video') {
        emitter.$emit('closeVideoPreview')
      }
      document.body.classList.remove('modal-opened')
    },
    toggleMaximize() {
      this.maximized = !this.maximized
    },
    checkmobile() {
      const isMobile = window.innerWidth <= this.maxWidth

      if (isMobile === this.mobile) return

      this.mobile = isMobile
    },
    pushDataLayer(name, type) {
      window.dataLayer.push({
        event: 'share',
        section: 'video',
        method: name,
        component_type: type
      })
    },
    pushDataLayerOpenShare() {
      // window.console.log(window.dataLayer)
      window.dataLayer.push({
        event: 'click',
        name: 'compartilhar',
        section: 'video',
        component_type: 'botao',
        outbound: false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .image {
    width: 100%;
    height: 100%;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }
</style>
